/* Borderline winter 26/27 · store opening line (P4: sections/bl-stores.liquid, snippets/bl-stores-row.liquid).
   <bl-store-hours> wraps the stores band's list. Every [data-hours] line inside it gets the store's state at the
   visitor's current moment in Toronto (all three stores are on Toronto time, whatever the visitor's own zone):
     "Open now"              open, more than an hour to closing        (green dot)
     "Closes 8 pm"           open, closing within the hour             (amber dot)
     "Opens 11 am"           opens later today                         (grey dot)
     "Opens tomorrow 12 pm" / "Opens Fri 12 pm"   closed for the day   (grey dot)
   followed by one more part:
     "Today 11 am – 8 pm"                     while today is still ahead (open now, or opening later today)
     "Daily 11 am – 8 pm" / "Fri – Sun 12 – 8 pm" / "Mon – Fri 10 am – 6 pm" / "Sat 10 am – 5 pm"
                                              once today is over or closed: the week's rule, when one span covers
                                              every open day and the open days are one run
     "Tomorrow 12 – 8 pm" / "Fri 12 – 8 pm"   an irregular week: the next day's hours
   On a one-off closed date (a holiday): "Closed today" (grey dot) followed by the next opening day's hours
     ("Closed today · Tomorrow 11 am – 8 pm"), never the week's rule.
   The words come from the element's data-l-* attributes (section settings). The line re-checks at the turn of every
   minute and when the tab is shown again; a line whose JSON gives no usable hours stays hidden.

   Hours JSON (the store metaobject's `hours` field, or the Store block's text):
     { "daily": "11:00-20:00" }                                every day the same
     { "mon": "11:00-20:00", "tue": "11:00-20:00", … }          per day; a day left out, "closed" or null = closed
     { "fri-sun": "12:00-20:00" }, { "weekdays": … }, { "weekends": … }   ranges (fri-sun runs through the weekend);
                                                                single days win over ranges, ranges over "daily"
     { "daily": "11:00-20:00", "2026-12-25": "closed", "2026-12-24": "10:00-16:00" }   one-off days win over the week
       (the same nested as "dates": { … } works in the metaobject; a theme setting refuses "}}", so keep blocks flat)
   A time is "11:00", "9:30", "20", "8 pm", "11am" or a number of hours (9.5); a span is "11:00-20:00",
   ["11:00", "20:00"] or { "open": "11:00", "close": "20:00" }. The prototype's { "days": [0…6], "open": 11,
   "close": 20 } (0 = Sunday), or a list of such rules, is read too. */

const TZ = 'America/Toronto';
const KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const ALL = [0, 1, 2, 3, 4, 5, 6];
const GROUPS = {
  daily: ALL,
  everyday: ALL,
  'every day': ALL,
  all: ALL,
  week: ALL,
  weekdays: [1, 2, 3, 4, 5],
  weekday: [1, 2, 3, 4, 5],
  weekends: [0, 6],
  weekend: [0, 6],
};
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const CLOSED = 'closed';

/** @typedef {[number, number] | 'closed'} Span  minutes from midnight, open and close; or closed all day */
/** @typedef {{ week: Span[], dates: Map<string, Span> }} Hours */

/** @param {unknown} word @returns {number} 0–6, or -1 */
function dayIndex(word) {
  const key = String(word).trim().toLowerCase().replace(/\.$/, '');
  if (!/^[a-z]{3,9}$/.test(key)) return -1;
  return KEYS.indexOf(key.slice(0, 3));
}

/** @param {string} key @returns {number[] | null} the days a key names: a group, a day, or a range of days */
function daysOf(key) {
  const k = key.trim().toLowerCase();
  if (k in GROUPS) return GROUPS[/** @type {keyof typeof GROUPS} */ (k)];
  const one = dayIndex(k);
  if (one >= 0) return [one];
  const range = k.match(/^([a-z]+)\.?\s*(?:-|–|—|to)\s*([a-z]+)\.?$/);
  if (!range) return null;
  const from = dayIndex(range[1]);
  const to = dayIndex(range[2]);
  if (from < 0 || to < 0) return null;
  const days = [];
  for (let d = from; ; d = (d + 1) % 7) {
    days.push(d);
    if (d === to) break;
  }
  return days;
}

/** @param {unknown} value @returns {number | null} minutes from midnight */
function minutes(value) {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 && value <= 24 ? Math.round(value * 60) : null;
  const m = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, ':')
    .match(/^(\d{1,2})(?::(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2] || 0);
  if (minute > 59) return null;
  if (m[3]) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (m[3].startsWith('p') ? 12 : 0);
  }
  if (hour > 24 || (hour === 24 && minute > 0)) return null;
  return hour * 60 + minute;
}

/** @returns {Span | undefined} */
function pair(open, close) {
  const o = minutes(open);
  const c = minutes(close);
  return o !== null && c !== null && c > o ? [o, c] : undefined;
}

/** @param {unknown} value @returns {Span | undefined} undefined when the value cannot be read */
function span(value) {
  if (value === null || value === false) return CLOSED;
  if (Array.isArray(value)) return value.length === 2 ? pair(value[0], value[1]) : undefined;
  if (typeof value === 'object') {
    const v = /** @type {Record<string, unknown>} */ (value);
    return pair(v.open ?? v.opens ?? v.from, v.close ?? v.closes ?? v.to);
  }
  const text = String(value ?? '').trim().toLowerCase();
  if (!text || ['closed', 'close', 'off', 'none', '-', '—'].includes(text)) return CLOSED;
  const parts = text.split(/\s*(?:–|—|-|to)\s*/);
  return parts.length === 2 ? pair(parts[0], parts[1]) : undefined;
}

/** @param {unknown} raw - JSON text or an already parsed value @returns {Hours | null} */
function readHours(raw) {
  let data = raw;
  if (typeof data === 'string') {
    if (!data.trim()) return null;
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object') return null;

  /** @type {(Span | undefined)[]} */
  const week = new Array(7).fill(undefined);
  /** @type {Map<string, Span>} */
  const dates = new Map();
  let found = false;

  /** @param {number[]} days @param {unknown} value */
  const setDays = (days, value) => {
    const sp = span(value);
    if (sp === undefined) return;
    for (const d of days) week[d] = sp;
    found = true;
  };
  /** @param {string} day @param {unknown} value */
  const setDate = (day, value) => {
    const sp = span(value);
    if (sp === undefined) return;
    dates.set(day, sp);
    found = true;
  };
  /** the prototype's rule: { days: [0–6 | "fri"], open, close } */
  const rule = (r) => {
    if (!r || typeof r !== 'object' || !Array.isArray(r.days)) return false;
    const days = r.days
      .map((d) => (typeof d === 'number' ? ((Math.trunc(d) % 7) + 7) % 7 : dayIndex(d)))
      .filter((d) => d >= 0);
    setDays(days, [r.open ?? r.opens, r.close ?? r.closes]);
    return true;
  };

  if (Array.isArray(data)) {
    for (const r of data) rule(r);
  } else if (!rule(data)) {
    /** @type {[number[], unknown][]} */
    const spans = [];
    for (const [key, value] of Object.entries(data)) {
      const k = key.trim().toLowerCase();
      if (['dates', 'special', 'holidays', 'exceptions'].includes(k)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          for (const [day, v] of Object.entries(value)) if (DATE_KEY.test(day.trim())) setDate(day.trim(), v);
        }
        continue;
      }
      if (DATE_KEY.test(k)) {
        setDate(k, value);
        continue;
      }
      const days = daysOf(k);
      if (days) spans.push([days, value]);
    }
    // the widest key first, so a single day overrides a range and a range overrides "daily", in any key order
    spans.sort((a, b) => b[0].length - a[0].length);
    for (const [days, value] of spans) setDays(days, value);
  }

  if (!found) return null;
  return { week: week.map((sp) => sp ?? CLOSED), dates };
}

const partsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** The moment in Toronto: its date, weekday (0 = Sunday) and minutes from midnight. */
function torontoNow(date = new Date()) {
  /** @type {Record<string, string>} */
  const p = {};
  for (const { type, value } of partsFormat.formatToParts(date)) p[type] = value;
  const hour = Number(p.hour) % 24;
  return {
    ymd: `${p.year}-${p.month}-${p.day}`,
    wd: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday),
    min: hour * 60 + Number(p.minute),
  };
}

/** @param {string} ymd @param {number} n */
function addDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** @param {Hours} hours @param {string} ymd @param {number} wd @returns {Span} */
function spanOn(hours, ymd, wd) {
  return hours.dates.has(ymd) ? /** @type {Span} */ (hours.dates.get(ymd)) : hours.week[wd];
}

/** @param {number} min */
function clock(min) {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return { t: m ? `${h % 12 || 12}:${String(m).padStart(2, '0')}` : `${h % 12 || 12}`, ap: h >= 12 ? 'pm' : 'am' };
}

const lang = () => document.documentElement.lang || 'en';
/** French pages (/fr: <html lang="fr">) write the 24-hour clock: "11 h", "9 h 30", "20 h" (R11) */
const isFr = () => lang().toLowerCase().startsWith('fr');

/** @param {number} min @returns {string} "11 h", "9 h 30" */
function clockFr(min) {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;   // no-break spaces: "11 h" never wraps
}

/** "11 am", "9:30 am", "8 pm" (French: "11 h", "9 h 30", "20 h") */
const time = (min) => {
  if (isFr()) return clockFr(min);
  const c = clock(min);
  return `${c.t} ${c.ap}`;
};

/** "11 am – 8 pm", "12 – 8 pm" (one meridiem when both share it, as the prototype writes "Fri – Sun 12 – 8 pm");
    French: "11 h – 20 h" */
const range = ([open, close]) => {
  if (isFr()) return `${clockFr(open)} – ${clockFr(close)}`;
  const a = clock(open);
  const b = clock(close);
  return a.ap === b.ap ? `${a.t} – ${b.t} ${b.ap}` : `${a.t} ${a.ap} – ${b.t} ${b.ap}`;
};

/** @param {number} wd */
const weekday = (wd) => new Intl.DateTimeFormat(lang(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2023, 0, 1 + wd)));

/** "Tomorrow" from "tomorrow": the word opens the part */
const upFirst = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * The week as one rule: "Daily 11 am – 8 pm", "Fri – Sun 12 – 8 pm", "Sat 10 am – 5 pm"; '' when the open days differ
 * in hours or fall in more than one run.
 * @param {Hours} hours
 * @param {Record<string, string>} words
 */
function weekRule(hours, words) {
  const open = hours.week.map((sp, d) => (sp === CLOSED ? -1 : d)).filter((d) => d >= 0);
  if (!open.length) return '';
  const first = /** @type {[number, number]} */ (hours.week[open[0]]);
  const same = open.every((d) => {
    const sp = /** @type {[number, number]} */ (hours.week[d]);
    return sp[0] === first[0] && sp[1] === first[1];
  });
  if (!same) return '';
  if (open.length === 7) return `${words.daily} ${range(first)}`;
  const isOpen = (d) => hours.week[((d % 7) + 7) % 7] !== CLOSED;
  const starts = open.filter((d) => !isOpen(d - 1));
  if (starts.length !== 1) return '';
  const start = starts[0];
  let end = start;
  for (let i = 1; i < 7 && isOpen(start + i); i++) end = (start + i) % 7;
  const days = end === start ? weekday(start) : `${weekday(start)} – ${weekday(end)}`;
  return `${days} ${range(first)}`;
}

/**
 * The second part once today is over (or closed): the week's rule, else the next day's hours, else "Closed today".
 * On a one-off closed date (a holiday) the week's rule would read as if the store opened today, so the next day's
 * hours are given instead.
 * @param {Hours} hours
 * @param {{ ymd: string, wd: number, min: number }} now
 * @param {Record<string, string>} words
 * @param {boolean} [holiday] - today is a one-off closed date
 */
function after(hours, now, words, holiday = false) {
  const rule = holiday ? '' : weekRule(hours, words);
  if (rule) return rule;
  for (let d = 1; d <= 14; d++) {
    const next = spanOn(hours, addDays(now.ymd, d), (now.wd + d) % 7);
    if (next === CLOSED) continue;
    return `${d === 1 ? upFirst(words.tomorrow) : weekday((now.wd + d) % 7)} ${range(next)}`;
  }
  return words.closedToday;
}

/**
 * @param {Hours} hours
 * @param {{ ymd: string, wd: number, min: number }} now
 * @param {Record<string, string>} words
 */
function status(hours, now, words) {
  const today = spanOn(hours, now.ymd, now.wd);
  // a one-off closed date (a holiday): "Closed today" and the next opening day's hours, never the week's rule
  const holiday = hours.dates.get(now.ymd) === CLOSED;
  const todayText = today !== CLOSED && now.min < today[1] ? `${words.today} ${range(today)}` : after(hours, now, words, holiday);

  if (today !== CLOSED && now.min >= today[0] && now.min < today[1]) {
    if (today[1] - now.min <= 60) return { state: 'closing', label: `${words.closes} ${time(today[1])}`, today: todayText };
    return { state: 'open', label: words.open, today: todayText };
  }
  if (today !== CLOSED && now.min < today[0]) {
    return { state: 'closed', label: `${words.opens} ${time(today[0])}`, today: todayText };
  }
  for (let d = 1; d <= 14; d++) {
    const next = spanOn(hours, addDays(now.ymd, d), (now.wd + d) % 7);
    if (next === CLOSED) continue;
    if (holiday) return { state: 'closed', label: words.closedToday, today: todayText };
    const when = d === 1 ? words.tomorrow : weekday((now.wd + d) % 7);
    return { state: 'closed', label: `${words.opens} ${when} ${time(next[0])}`, today: todayText };
  }
  return { state: 'closed', label: '', today: todayText };
}

class BlStoreHours extends HTMLElement {
  #timer = 0;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {WeakMap<Element, { raw: string, hours: Hours | null }>} */
  #cache = new WeakMap();

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(
      'visibilitychange',
      () => {
        if (!document.hidden) this.update();
      },
      { signal: this.#controller.signal }
    );
    this.update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }

  get words() {
    const d = this.dataset;
    return {
      open: d.lOpen || 'Open now',
      closes: d.lCloses || 'Closes',
      opens: d.lOpens || 'Opens',
      tomorrow: d.lTomorrow || 'tomorrow',
      today: d.lToday || 'Today',
      daily: d.lDaily || 'Daily',
      closedToday: d.lClosedToday || 'Closed today',
    };
  }

  /** Write every line for this minute, then wait for the next one. */
  update() {
    window.clearTimeout(this.#timer);
    let now;
    try {
      now = torontoNow();
    } catch {
      return;
    }
    const words = this.words;
    for (const line of this.querySelectorAll('[data-hours]')) {
      if (!(line instanceof HTMLElement)) continue;
      const raw = line.dataset.hours || '';
      let entry = this.#cache.get(line);
      if (!entry || entry.raw !== raw) {
        entry = { raw, hours: readHours(raw) };
        this.#cache.set(line, entry);
      }
      const stateEl = line.querySelector('[data-bl-state]');
      const todayEl = line.querySelector('[data-bl-today]');
      if (!entry.hours || now.wd < 0 || !stateEl || !todayEl) {
        line.hidden = true;
        continue;
      }
      const result = status(entry.hours, now, words);
      line.dataset.status = result.state;
      stateEl.textContent = result.label;
      todayEl.textContent = result.today;
      line.hidden = false;
    }
    if (this.isConnected) this.#timer = window.setTimeout(() => this.update(), 60000 - (Date.now() % 60000) + 50);
  }
}

if (!customElements.get('bl-store-hours')) customElements.define('bl-store-hours', BlStoreHours);

/* exposed for checks in the console / tests: BL.storeHours.status(BL.storeHours.read('{"daily":"11:00-20:00"}'), BL.storeHours.now()) */
window.BL = window.BL || {};
window.BL.storeHours = { read: readHours, now: torontoNow, status: (hours, now, words) => status(hours, now, { open: 'Open now', closes: 'Closes', opens: 'Opens', tomorrow: 'tomorrow', today: 'Today', daily: 'Daily', closedToday: 'Closed today', ...words }) };
